import React, { FC } from 'react';

import { AlertResponse, NotificationFor } from '@ariksa/notification/api';
import { useDisclosure } from '@chakra-ui/react';
import { isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { isAlertVaultEnabled } from 'api/alert-vault/client';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { AlertDetailsDrawer } from 'containers/Findings/AlertDrawer/Components/Aggregated/AlertDetailsDrawer';
import { RemediationModal } from 'containers/Findings/AlertDrawer/Components/Individual/Remediation/RemediationModal';
import { AddException } from 'containers/Findings/Alerts/Components/AddException';
import { useAlerts } from 'containers/Findings/Alerts/Components/AlertsTable/hooks/useAlerts';
import { useIndividualAlertsTableColumns } from 'containers/Findings/Alerts/Components/AlertsTable/hooks/useIndividualAlertsTableColumns';
import { CreateNotification } from 'containers/Findings/Alerts/Components/CreateNotification';
import { CreateTicket } from 'containers/Findings/Alerts/Components/CreateTicket';
import { SnoozeAlert } from 'containers/Findings/Alerts/Components/SnoozeAlert';
import { selectAlerts } from 'containers/Findings/Alerts/selectors';
import { actions } from 'containers/Findings/Alerts/slice';

export const IndividualAlertsTable: FC = () => {
  const dispatch = useDispatch();
  const { alerts, filters, sortByField } = useSelector(selectAlerts);
  const alertDrawer = useDisclosure();

  const {
    columns,
    ticketModal,
    notificationModal,
    currentRecord,
    snooze,
    exceptionModal,
    remediate,
    setCurrentRecord,
    changeSeverity,
    setChangeSeverity,
  } = useIndividualAlertsTableColumns();
  const { getAlerts } = useAlerts();

  return (
    <>
      <Table
        data={alerts.data}
        columns={columns}
        isLoading={alerts.isLoading}
        onRowClick={row => {
          setCurrentRecord(row);
          alertDrawer.onOpen();
        }}
        pagination={{
          pageInfo: alerts.page.info,
          onChange: info => dispatch(actions.updateAlertsPagination(info)),
          totalCount: alerts.page.totalCount,
          cursorBased: isAlertVaultEnabled(),
          // Alert Vault alert lists return the exact total.
          exactTotal: isAlertVaultEnabled(),
        }}
        sortBy={sortByField}
        onSort={sortInfo => {
          if (!isEqual(sortByField, sortInfo)) {
            dispatch(actions.updateSortInfo(sortInfo));
          }
        }}
        styles={{ header: { zIndex: 800 } }}
      />
      {alertDrawer.isOpen && (
        <AlertDetailsDrawer
          alert={currentRecord as AlertResponse}
          isOpen={alertDrawer.isOpen}
          onClose={alertDrawer.onClose}
        />
      )}
      {snooze.isOpen && (
        <SnoozeAlert
          alert={currentRecord as AlertResponse}
          onClose={snooze.onClose}
          isOpen={snooze.isOpen}
          type={filters.alertType === 'Snoozed' ? 'unsnooze' : 'snooze'}
          onSuccess={() => getAlerts()}
        />
      )}
      {remediate.isOpen && (
        <RemediationModal
          alert={currentRecord as AlertResponse}
          state={remediate}
        />
      )}
      {exceptionModal.isOpen && (
        <AddException
          alert={currentRecord as AlertResponse}
          onClose={() => {
            exceptionModal.onClose();
            setChangeSeverity(false);
          }}
          isOpen={exceptionModal.isOpen}
          onSuccess={() => getAlerts()}
          exceptionFor="Issue"
          changeSeverity={changeSeverity}
        />
      )}

      {ticketModal.isOpen && (
        <CreateTicket
          onClose={ticketModal.onClose}
          isOpen={ticketModal.isOpen}
          alert={currentRecord}
          onCreate={() => getAlerts()}
          notification_for={NotificationFor.Alerts}
        />
      )}
      {notificationModal.isOpen && (
        <CreateNotification
          onClose={notificationModal.onClose}
          isOpen={notificationModal.isOpen}
          alert={currentRecord}
          onCreate={() => getAlerts()}
          notification_for={NotificationFor.Alerts}
        />
      )}
    </>
  );
};
